import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { useSelection } from "src/hooks/useSelection";
import { SearchContext } from "src/search/useSearchContext";

export enum ContentViews {
  "default" = "default",
  "barChart" = "barChart",
  "dataTable" = "dataTable",
  "hex" = "hex",
  "heatMap" = "heatMap",
  "legend" = "legend",
  "labels" = "labels",
  "metric" = "metric",
  "overTime" = "overTime",
  "pivotTable" = "pivotTable",
  "treeMap" = "treeMap",
  "nestedTable" = "nestedTable",
  "unifiedTable" = "unifiedTable",
}

export type ContentView = ReturnType<typeof useSelection<ContentViews>> & {
  config: any;
  setConfig: Dispatch<SetStateAction<any>>;
};

export type SerializableView = Pick<ContentView, "config" | "value">;

export function useContentView(
  searchContext: SearchContext,
  initial: SerializableView | undefined
): ContentView {
  const view = useSelection(
    [
      ContentViews.barChart,
      ContentViews.dataTable,
      ContentViews.default,
      ContentViews.heatMap,
      ContentViews.hex,
      ContentViews.labels,
      ContentViews.legend,
      ContentViews.metric,
      ContentViews.overTime,
      ContentViews.treeMap,
      ContentViews.nestedTable,
      ContentViews.unifiedTable,
    ],
    initial?.value || ContentViews.default
  );
  const [config, setConfig] = useState(initial?.config || {});

  /**
   * Time to get fancy.
   * 1. When the view value switches to `default` we will
   *    determine what the view value should be, based on
   *    the search context params.
   * 2. In addition, we'll set an interval. During this
   *    interval, we'll continue set the default views
   *    as the search params change.
   * 3. After a moment in time, we'll stop.
   * */
  const [defaultInterval, setDefaultInterval] = useState(0);
  useEffect(() => {
    if (view.value === ContentViews.default)
      setDefaultInterval(Date.now() + 500); // 1/2 second
  }, [view.value]);

  if (view.value === ContentViews.default || Date.now() < defaultInterval) {
    // we are in the period of time where we should
    // set the default view value based on searchContext stuff.
    const groupByCount = searchContext.params.selectors.countGroupBys(
      searchContext.params.state
    );
    if (groupByCount === 0 && view.value !== ContentViews.unifiedTable)
      view.set(ContentViews.unifiedTable);
    if (groupByCount === 1 && view.value !== ContentViews.barChart)
      view.set(ContentViews.barChart);
    if (groupByCount >= 2 && view.value !== ContentViews.unifiedTable)
      view.set(ContentViews.unifiedTable);
  }

  return { ...view, config, setConfig };
}

export const serializeContentView = (v?: Partial<SerializableView>) =>
  JSON.stringify({
    value: v?.value || ContentViews.default,
    config: v?.config || {},
  });

export const unserializeContentView: (enc: string) => SerializableView = (
  encoded
) => {
  try {
    const data = JSON.parse(encoded);
    if (
      typeof data === "object" &&
      data.config &&
      data.value &&
      isContentView(data.value)
    ) {
      return data;
    }
  } catch {}
  return {
    config: {},
    value: ContentViews.default,
  };
};

export function isContentView(value: string | unknown): value is ContentViews {
  return Object.values(ContentViews).includes(value as ContentViews);
}

export function toSessionStorageView(view: ContentView) {
  const { config, value } = view;
  return { config, value };
}
